import type { LeadCaptureContext, ServiceQualificationResponse } from '@t/serviceQualification.types';

/**
 * The two address ids the order-funnel events report, in domain terms.
 * `buildOrderAddressIds` renames them to `amsAddressId` / `gldsHomeId` at the
 * analytics boundary.
 */
export type OrderAnalyticsQualification = {
  qualifiedAddressId: string | null;
  resolvedAddressId: string | null;
};

/**
 * Projects qualification-flow state onto the order-funnel reporting shape.
 * `null` means "this visitor has not entered the order funnel, so do not
 * report" (SC-13175).
 *
 * Two terms, because two distinct states must both suppress the report:
 *   - `qualificationResult === null` — no address check has resolved yet, or it
 *     answered AMS-404 / no-qualified-address (`qualificationRouter` writes a
 *     null result there, as does the SYSTEM_ERROR path).
 *   - `leadCaptureContext !== null` — the check resolved but routed the visitor
 *     to Lead Capture (off-net, BUSINESS, NON_BOOKABLE, PLANNED, CONSTRUCTION,
 *     fallback). Those all go through `buildSuccessStoreUpdate`, which writes a
 *     *non-null* result, so the first term alone lets them through.
 *     `buildSuccessStoreUpdate(result, null)` is the only PRODUCT_LANDING write,
 *     which makes a null context the router's own signal that this visitor
 *     reached the order funnel. Lead-funnel visitors are counted by
 *     `lead_started`, so reporting them here would double-count them.
 *
 * `resolvedAddressId` is taken from the store's canonical top-level field rather
 * than from `qualificationResult.resolvedAddressId`. `buildSuccessStoreUpdate`
 * writes both from the same source, but the seven other sites that hand-build
 * this shape all read the top-level field (the CONTEXT.md two-ID rule), and this
 * function is meant to become their shared adapter — sourcing it differently
 * would make it non-substitutable for them.
 *
 * Lives in `utils/`, not in `services/analytics/`, because the analytics
 * subgraph is dynamically imported and no app-side file may statically reach it
 * (`containment.test.ts`). Address fields deliberately do not cross this seam —
 * `serviceAddress` is PII and is descoped from every order event.
 */
export const toOrderAnalyticsQualification = (qualification: {
  qualificationResult: ServiceQualificationResponse | null;
  leadCaptureContext: LeadCaptureContext | null;
  resolvedAddressId: string | null;
}): OrderAnalyticsQualification | null => {
  const { qualificationResult, leadCaptureContext, resolvedAddressId } = qualification;
  if (qualificationResult === null || leadCaptureContext !== null) return null;
  return {
    qualifiedAddressId: qualificationResult.serviceQualificationItem?.qualifiedAddress?.id ?? null,
    resolvedAddressId,
  };
};
